import React, { Suspense, createContext, lazy, useContext, useEffect, useState } from 'react';
import { HashRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Product, CartItem, AppRoute, User } from './types';
import { CURRENT_USER } from './constants';
import { ToastHost } from './components/UI';
import { seedPocData } from './services/pocRuntime';
import { KioskControls } from './components/KioskControls';
import { KioskPreferencesProvider, useKioskPreferences } from './services/kioskPreferences';

const WelcomeScreen = lazy(() =>
  import('./pages/KioskScreens').then((m) => ({ default: m.WelcomeScreen })),
);
const PersonalizedHomeScreen = lazy(() =>
  import('./pages/KioskScreens').then((m) => ({ default: m.PersonalizedHomeScreen })),
);
const MenuScreen = lazy(() =>
  import('./pages/KioskScreens').then((m) => ({ default: m.MenuScreen })),
);
const CustomizeScreen = lazy(() =>
  import('./pages/KioskScreens').then((m) => ({ default: m.CustomizeScreen })),
);
const SummaryScreen = lazy(() =>
  import('./pages/KioskScreens').then((m) => ({ default: m.SummaryScreen })),
);
const UpsellScreen = lazy(() =>
  import('./pages/KioskScreens').then((m) => ({ default: m.UpsellScreen })),
);
const PaymentScreen = lazy(() =>
  import('./pages/KioskScreens').then((m) => ({ default: m.PaymentScreen })),
);
const ConfirmationScreen = lazy(() =>
  import('./pages/KioskScreens').then((m) => ({ default: m.ConfirmationScreen })),
);
const AdminDashboard = lazy(() =>
  import('./pages/AdminScreens').then((m) => ({ default: m.AdminDashboard })),
);
const AdminInventory = lazy(() =>
  import('./pages/AdminScreens').then((m) => ({ default: m.AdminInventory })),
);

// Context Definitions
interface AppContextType {
  user: User | null;
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (cartId: string) => void;
  clearCart: () => void;
  login: () => void;
  logout: () => void;
  selectedProduct: Product | null;
  setSelectedProduct: (p: Product | null) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useApp must be used within AppProvider");
  return context;
};

// Main Component
const App = () => {
  const [user, setUser] = useState<User | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    seedPocData();
  }, []);

  const addToCart = (item: CartItem) => {
    setCart((prev) => [...prev, item]);
  };

  const removeFromCart = (cartId: string) => {
    setCart((prev) => prev.filter(item => item.cartId !== cartId));
  };

  const clearCart = () => setCart([]);

  const login = () => setUser(CURRENT_USER);
  const logout = () => setUser(null);

  const RoutedScreens = () => {
    const location = useLocation();
    const { tr } = useKioskPreferences();

    useEffect(() => {
      const routeTitles: Record<string, string> = {
        [AppRoute.WELCOME]: tr("Accueil", "Welcome"),
        [AppRoute.HOME]: tr("Accueil personnalisé", "Personalized home"),
        [AppRoute.MENU]: tr("Menu", "Menu"),
        [AppRoute.CUSTOMIZE]: tr("Personnalisation", "Customization"),
        [AppRoute.SUMMARY]: tr("Récapitulatif", "Summary"),
        [AppRoute.UPSELL]: tr("Upsell", "Upsell"),
        [AppRoute.PAYMENT]: tr("Paiement", "Payment"),
        [AppRoute.CONFIRMATION]: tr("Confirmation", "Confirmation"),
        [AppRoute.ADMIN_DASHBOARD]: tr("Admin - Tableau de bord", "Admin - Dashboard"),
        [AppRoute.ADMIN_INVENTORY]: tr("Admin - Inventaire", "Admin - Inventory"),
      };
      document.title = `BÔBA LÀ · ${routeTitles[location.pathname] ?? "POC"}`;
    }, [location.pathname, tr]);

    return (
      <Suspense
        fallback={
          <div className="min-h-[45vh] flex items-center justify-center text-primary/70 text-sm font-bold">
            Chargement...
          </div>
        }
      >
        <Routes>
          <Route path={AppRoute.WELCOME} element={<WelcomeScreen />} />
          <Route path={AppRoute.HOME} element={<PersonalizedHomeScreen />} />
          <Route path={AppRoute.MENU} element={<MenuScreen />} />
          <Route path={AppRoute.CUSTOMIZE} element={<CustomizeScreen />} />
          <Route path={AppRoute.SUMMARY} element={<SummaryScreen />} />
          <Route path={AppRoute.UPSELL} element={<UpsellScreen />} />
          <Route path={AppRoute.PAYMENT} element={<PaymentScreen />} />
          <Route path={AppRoute.CONFIRMATION} element={<ConfirmationScreen />} />

          {/* Admin Routes */}
          <Route path={AppRoute.ADMIN_DASHBOARD} element={<AdminDashboard />} />
          <Route path={AppRoute.ADMIN_INVENTORY} element={<AdminInventory />} />
        </Routes>
      </Suspense>
    );
  };

  const AppFrame = () => {
    const { accessibilityClassName, tr } = useKioskPreferences();

    return (
      <HashRouter>
        <div className={`min-h-screen w-full font-sans text-primary bg-cream overflow-x-hidden selection:bg-primary selection:text-white ${accessibilityClassName}`}>
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-[110] focus:bg-white focus:text-primary focus:rounded-lg focus:px-3 focus:py-2 focus:font-bold focus:shadow-md"
          >
            {tr("Aller au contenu principal", "Skip to main content")}
          </a>
          <main id="main-content">
            <RoutedScreens />
          </main>
          <KioskControls />
          <ToastHost />
        </div>
      </HashRouter>
    );
  };

  return (
    <AppContext.Provider value={{
      user,
      cart,
      addToCart,
      removeFromCart,
      clearCart,
      login,
      logout,
      selectedProduct,
      setSelectedProduct
    }}>
      <KioskPreferencesProvider>
        <AppFrame />
      </KioskPreferencesProvider>
    </AppContext.Provider>
  );
};

export default App;
